import { Component } from '@theme/component';

/**
 * Horizon markup the filter depends on. Re-check these after a theme update.
 */
const SELECTORS = {
  main: '#MainContent',
  resultsList: 'results-list',
  grid: '[ref="grid"]',
  gridItem: '.product-grid__item',
  count: '.products-count-wrapper [role="status"]',
  viewMoreNext: '[ref="viewMoreNext"]',
};

const STORAGE_PREFIX = 'delivery-filter:';

/**
 * Turns the open-delivery cards on a collection page into a single-select toggle filter over the
 * product grid. Each toggle and the stored filter carry the delivery window's handle, never its
 * position, so a filter stored before a rollover cannot select another delivery after it. Hiding is
 * done by CSS the section renders for the open handles, keyed off `data-delivery-filter` on
 * `<main>`; this element only owns that attribute and keeps the count, announcement, empty state
 * and session storage in step with it.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement[]} [toggles] - The card toggle buttons.
 * @property {HTMLElement} [emptyState] - The no-match message container.
 * @property {HTMLElement} [emptyMessage] - The no-match message text.
 * @property {HTMLElement} [result] - The result line shown while a filter matches products.
 * @property {HTMLElement} [resultText] - The result line's text.
 * @property {HTMLElement} [liveRegion] - The polite live region for result announcements.
 *
 * @extends Component<Refs>
 */
class DeliveryCardsFilter extends Component {
  /**
   * Server-rendered and last-written text per products-count element.
   *
   * @type {WeakMap<Element, { server: string, written: string }>}
   */
  #countText = new WeakMap();

  /** @type {MutationObserver} */
  #observer = new MutationObserver(() => this.#scheduleRefresh());

  #frame = 0;

  #itemCount = 0;

  connectedCallback() {
    super.connectedCallback();

    for (const toggle of this.#toggles) toggle.hidden = false;

    const stored = this.#readStored();
    if (stored && this.#toggleFor(stored)) {
      this.#apply(stored);
    } else {
      if (stored) this.#writeStored(null);
      this.#syncPressed(this.#current);
    }

    const target = document.querySelector(SELECTORS.resultsList)?.closest('.shopify-section') ?? this.#main;
    if (target) {
      this.#observer.observe(target, { childList: true, subtree: true, characterData: true });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer.disconnect();
    cancelAnimationFrame(this.#frame);
    this.#frame = 0;
    if (this.#main) delete this.#main.dataset.deliveryFilter;
  }

  /**
   * Toggles the filter for the clicked card: on, switched from the other card, or off when the
   * selected card is activated again.
   *
   * @param {Event} event
   */
  toggle(event) {
    const toggle = event.target;
    if (!(toggle instanceof HTMLButtonElement)) return;

    const value = toggle.dataset.deliveryFilter ?? null;
    this.#apply(this.#current === value ? null : value, true);
  }

  /**
   * Clears the filter from the result line or the empty state and returns focus to the card that was selected.
   */
  showAll() {
    const previous = this.#current ? this.#toggleFor(this.#current) : undefined;
    this.#apply(null, true);
    previous?.focus();
  }

  /**
   * @param {string | null} value - The delivery window handle to filter to, or null to clear.
   * @param {boolean} [announce] - Whether to announce the result in the live region.
   */
  #apply(value, announce = false) {
    const main = this.#main;
    if (!main) return;

    if (value) {
      main.dataset.deliveryFilter = value;
    } else {
      delete main.dataset.deliveryFilter;
    }

    this.#writeStored(value);
    this.#syncPressed(value);

    const shown = this.#refresh();
    if (announce) this.#announce(value, shown);
    this.#rearmInfiniteScroll();
  }

  /**
   * Re-applies the count and empty state after the grid changes underneath the filter.
   */
  #scheduleRefresh() {
    if (this.#frame) return;

    this.#frame = requestAnimationFrame(() => {
      this.#frame = 0;
      const previousCount = this.#itemCount;
      this.#refresh();
      if (this.#itemCount > previousCount) this.#rearmInfiniteScroll();
    });
  }

  /**
   * @returns {number} The number of grid items currently shown.
   */
  #refresh() {
    const value = this.#current;
    const items = this.#items;
    this.#itemCount = items.length;

    const shown = value
      ? items.filter((item) => item.querySelector(`.product-card-delivery__strip[data-delivery-offered~="${CSS.escape(value)}"]`))
          .length
      : items.length;

    this.#updateCount(value ? shown : null);
    this.#updateResult(value, shown);
    this.#updateEmptyState(value, shown);

    return shown;
  }

  /**
   * @param {number | null} count - The filtered count, or null to restore the server text.
   */
  #updateCount(count) {
    for (const element of document.querySelectorAll(SELECTORS.count)) {
      const current = element.textContent ?? '';
      let state = this.#countText.get(element);

      if (!state || current !== state.written) {
        state = { server: current, written: current };
        this.#countText.set(element, state);
      }

      const text =
        count === null ? state.server : this.#pluralize(this.dataset.countOne, this.dataset.countOther, count);

      if (current !== text) element.textContent = text;
      state.written = text;
    }
  }

  /**
   * Shows what the filtered grid holds while a filter is on and matches products. It reads the
   * selected toggle's announcement text, so the line and the live region say the same thing.
   *
   * @param {string | null} value
   * @param {number} shown
   */
  #updateResult(value, shown) {
    const { result, resultText } = this.refs;
    if (!result) return;

    const toggle = value && shown > 0 ? this.#toggleFor(value) : undefined;

    if (toggle && resultText) {
      const text = this.#pluralize(toggle.dataset.announceOne, toggle.dataset.announceOther, shown);
      if (resultText.textContent !== text) resultText.textContent = text;
    }

    result.hidden = !toggle;
  }

  /**
   * @param {string | null} value
   * @param {number} shown
   */
  #updateEmptyState(value, shown) {
    const { emptyState, emptyMessage } = this.refs;
    if (!emptyState) return;

    const isEmpty = Boolean(value) && shown === 0 && !this.#hasMorePages;

    if (isEmpty && value && emptyMessage) {
      emptyMessage.textContent = this.#toggleFor(value)?.dataset.emptyMessage ?? '';
    }

    emptyState.hidden = !isEmpty;
  }

  /**
   * @param {string | null} value
   * @param {number} shown
   */
  #announce(value, shown) {
    const { liveRegion } = this.refs;
    if (!liveRegion) return;

    const toggle = value ? this.#toggleFor(value) : undefined;
    const text = toggle
      ? this.#pluralize(toggle.dataset.announceOne, toggle.dataset.announceOther, shown)
      : this.#pluralize(this.dataset.clearedOne, this.dataset.clearedOther, shown);

    liveRegion.textContent = '';
    requestAnimationFrame(() => {
      liveRegion.textContent = text;
    });
  }

  /**
   * PaginatedList only loads the next page when its sentinel changes to intersecting. When a whole
   * appended page is hidden the sentinel never leaves the viewport, so observing it again delivers
   * a fresh initial entry and keeps loading going.
   */
  #rearmInfiniteScroll() {
    if (!this.#current) return;

    const resultsList = /** @type {(HTMLElement & { infinityScrollObserver?: IntersectionObserver }) | null} */ (
      document.querySelector(SELECTORS.resultsList)
    );
    const observer = resultsList?.infinityScrollObserver;
    const sentinel = resultsList?.querySelector(SELECTORS.viewMoreNext);

    if (!(observer instanceof IntersectionObserver) || !sentinel) return;

    observer.unobserve(sentinel);
    observer.observe(sentinel);
  }

  /**
   * @param {string | null} value
   */
  #syncPressed(value) {
    for (const toggle of document.querySelectorAll('delivery-cards-filter .delivery-cards__toggle')) {
      toggle.setAttribute('aria-pressed', String(toggle.getAttribute('data-delivery-filter') === value));
    }
  }

  /**
   * @param {string | undefined} one
   * @param {string | undefined} other
   * @param {number} count
   * @returns {string}
   */
  #pluralize(one, other, count) {
    const template = (count === 1 ? one : other) ?? '';
    return template.replace(this.dataset.countPlaceholder ?? '', String(count));
  }

  /**
   * @param {string} value
   * @returns {HTMLButtonElement | undefined}
   */
  #toggleFor(value) {
    return this.#toggles.find((toggle) => toggle.dataset.deliveryFilter === value);
  }

  /** @returns {string | null} */
  #readStored() {
    try {
      return sessionStorage.getItem(this.#storageKey);
    } catch {
      return null;
    }
  }

  /**
   * @param {string | null} value
   */
  #writeStored(value) {
    try {
      if (value) {
        sessionStorage.setItem(this.#storageKey, value);
      } else {
        sessionStorage.removeItem(this.#storageKey);
      }
    } catch {
      // Storage unavailable: the filter still works for this page view.
    }
  }

  get #storageKey() {
    return `${STORAGE_PREFIX}${this.dataset.collectionHandle ?? ''}`;
  }

  get #hasMorePages() {
    const resultsList = document.querySelector(SELECTORS.resultsList);
    const grid = resultsList?.querySelector(SELECTORS.grid);
    if (!(grid instanceof HTMLElement) || !resultsList?.querySelector(SELECTORS.viewMoreNext)) return false;

    const items = this.#items;
    const lastLoadedPage = Number(items[items.length - 1]?.dataset.page);

    return lastLoadedPage < Number(grid.dataset.lastPage);
  }

  /** @returns {HTMLElement[]} */
  get #items() {
    const resultsList = document.querySelector(SELECTORS.resultsList);
    return resultsList ? Array.from(resultsList.querySelectorAll(SELECTORS.gridItem)) : [];
  }

  /** @returns {HTMLButtonElement[]} */
  get #toggles() {
    return this.refs.toggles ?? [];
  }

  /** @returns {string | null} */
  get #current() {
    return this.#main?.dataset.deliveryFilter ?? null;
  }

  /** @returns {HTMLElement | null} */
  get #main() {
    return document.querySelector(SELECTORS.main);
  }
}

if (!customElements.get('delivery-cards-filter')) {
  customElements.define('delivery-cards-filter', DeliveryCardsFilter);
}
